import { styled } from '@mui/material';
import React, { ReactNode } from 'react';
import { ListColorsType } from '@/constants/listTypes';

interface ListProps {
  id?: string | null | undefined;
  children: ReactNode;
  color: ListColorsType;
  listType: 'unordered' | 'ordered';
  columns?: number | null;
}

const renderListItems = (children: ReactNode) => {
  return React.Children.map(children, (child, index) => <li key={index}>{child}</li>);
};

export const List = ({ id, children, color = 'primary', listType = 'unordered', columns }: ListProps) => {
  if (listType === 'ordered') {
    return (
      <OrderedList
        id={id ?? undefined}
        color={color}
        columns={columns}
      >
        {renderListItems(children)}
      </OrderedList>
    );
  }

  return (
    <UnorderedList
      id={id ?? undefined}
      color={color}
      columns={columns}
    >
      {renderListItems(children)}
    </UnorderedList>
  );
};

const listElementFactory = (listType: 'unordered' | 'ordered') => {
  return styled(listType === 'unordered' ? 'ul' : 'ol')<{ color: ListColorsType; columns?: number | null }>`
    margin: 0;
    padding-left: 1.5rem;
    color: ${({ theme, color }) => theme.palette.text[color]};

    ${({ columns }) =>
      columns
        ? `
          /* CSS Columns layout */
          column-count: ${columns};
          column-gap: 1.5rem;
          
          /* Prevent list items from breaking across columns */
          li {
            break-inside: avoid;
            margin-bottom: 0.5rem;
          }
        `
        : `
          /* Flexbox layout */
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
  `;
};

const OrderedList = listElementFactory('ordered');
const UnorderedList = listElementFactory('unordered');
